export type WorkspaceCommandStatus = "available" | "coming-soon";

export type WorkspaceCommand = {
  cmd: "/recommend" | "/report" | "/journey" | "/dashboard";
  label: string;
  desc: string;
  status: WorkspaceCommandStatus;
};

export type ParsedWorkspaceCommand = WorkspaceCommand & {
  argument: string;
};

export type WorkspacePromptRoute =
  | { kind: "query"; prompt: string; command?: undefined }
  | { kind: "recommend"; prompt: string; command: ParsedWorkspaceCommand }
  | { kind: "report"; prompt: string; command: ParsedWorkspaceCommand }
  | { kind: "journey"; prompt: string; command: ParsedWorkspaceCommand }
  | { kind: "dashboard"; prompt: string; command: ParsedWorkspaceCommand };

export const WORKSPACE_COMMANDS: WorkspaceCommand[] = [
  {
    cmd: "/recommend",
    label: "Recommend content",
    desc: "Find ranked journeys, courses, and events",
    status: "available",
  },
  {
    cmd: "/report",
    label: "Generate report",
    desc: "Build a structured report from the answer",
    status: "coming-soon",
  },
  {
    cmd: "/journey",
    label: "Create learning journey",
    desc: "Draft a multi-module journey",
    status: "coming-soon",
  },
  {
    cmd: "/dashboard",
    label: "Compose dashboard",
    desc: "Open a new dashboard with this query",
    status: "coming-soon",
  },
];

export const parseWorkspaceCommand = (value: string): ParsedWorkspaceCommand | undefined => {
  const trimmed = value.trimStart();
  if (!trimmed.startsWith("/")) {
    return undefined;
  }
  const [commandText = ""] = trimmed.split(/\s/, 1);
  const command = WORKSPACE_COMMANDS.find((entry) => entry.cmd === commandText.toLowerCase());
  if (!command) {
    return undefined;
  }
  return {
    ...command,
    argument: trimmed.slice(commandText.length).trim(),
  };
};

export const resolveWorkspacePromptRoute = (value: string): WorkspacePromptRoute => {
  const prompt = value.trim();
  const command = parseWorkspaceCommand(prompt);

  if (!command) {
    return { kind: "query", prompt };
  }
  if (command.status === "coming-soon") {
    return { kind: "query", prompt };
  }
  if (command.cmd === "/recommend") {
    return { kind: "recommend", prompt: command.argument, command };
  }
  if (command.cmd === "/report") {
    return { kind: "report", prompt: command.argument || prompt, command };
  }
  if (command.cmd === "/journey") {
    return { kind: "journey", prompt: command.argument || prompt, command };
  }
  return { kind: "dashboard", prompt: command.argument || prompt, command };
};

export type ConversationMode = "query" | "recommend";

export const resolveWorkspacePromptRouteWithMode = (
  value: string,
  conversationMode?: ConversationMode,
): WorkspacePromptRoute => {
  const route = resolveWorkspacePromptRoute(value);
  if (conversationMode !== "recommend" || route.kind !== "query") {
    return route;
  }
  const recommendCommand = WORKSPACE_COMMANDS.find((entry) => entry.cmd === "/recommend");
  if (!recommendCommand) {
    return route;
  }
  return {
    kind: "recommend",
    prompt: route.prompt,
    command: { ...recommendCommand, argument: route.prompt },
  };
};

export const stripWorkspaceCommand = (value: string) => {
  const command = parseWorkspaceCommand(value);
  return command ? command.argument || command.cmd : value;
};
